(function () {
  'use strict';

  // Endpoints can be overridden per section via data-* attributes set in the Liquid section.
  // These constants act as fallback defaults when the section setting is empty.
  // Hardcoded merchant backend that receives the validated form + cart payload.
  var SUBMIT_ENDPOINT = 'https://api.tante-e.com/richroyal/draft-order';
  // var SUBMIT_ENDPOINT = ' https://strainer-thumb-unmade.ngrok-free.dev/richroyal/draft-order';
  var DEFAULT_CART_UPDATE_URL = '/cart/update.js';
  var STORAGE_KEY = 'fakeCheckoutSnapshot';
  var CART_CLEARED_KEY = 'fakeCheckoutCartCleared';

  // Format patterns for client-side validation. The address pattern accepts
  // "Street name <space> number" with optional letter suffix (e.g. "Hauptstr. 12a").
  // Letter classes include the common German diacritics used on the storefront.
  // Email accepts the standard local@domain.tld shape — dots, digits, +/-/_/%
  // in the local part and a 2+ letter TLD.
  var EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-zÄÖÜäöüß]{2,}$/;
  var PHONE_PATTERN = /^[+()0-9\s/-]{6,}$/;
  var ADDRESS_PATTERN = /^[A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß.\- ]*\s\d+[A-Za-z]?$/;
  var ZIP_PATTERN = /^\d{5}$/;
  var CITY_PATTERN = /^[A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß\- ]*$/;
  var VALIDATION_PATTERNS = {
    email: EMAIL_PATTERN,
    phone: PHONE_PATTERN,
    address: ADDRESS_PATTERN,
    zip: ZIP_PATTERN,
    city: CITY_PATTERN
  };

  function formatMoney(cents, currency, locale) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (err) {
      return amount.toFixed(2);
    }
  }

  function formatAmount(cents, locale) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (err) {
      return amount.toFixed(2);
    }
  }

  function safeJsonParse(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (err) {
      return null;
    }
  }

  function readSnapshot() {
    try {
      return safeJsonParse(window.sessionStorage.getItem(STORAGE_KEY));
    } catch (err) {
      return null;
    }
  }

  function writeSnapshot(payload) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // sessionStorage may be disabled - fall back to in-memory only
    }
  }

  function clearSnapshot() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // ignore
    }
  }

  function fetchCart(cartUrl) {
    return fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) throw new Error('Failed to load cart');
      return response.json();
    });
  }

  function pluralizeSubtotalLabel(label, count) {
    return label.replace('{{ count }}', count).replace('{{count}}', count);
  }

  // Builds the canonical snapshot used by both the checkout summary and the thank-you page.
  // Keeping a single shape avoids divergent rendering paths.
  function normaliseCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;

    // Track unique discount codes across the cart. Each entry sums the total
    // amount allocated by that code so the chip list reflects the true saving.
    var seen = {};
    var discountCodes = [];
    function pushCode(title, amount) {
      if (!title) return;
      if (seen[title] != null) {
        discountCodes[seen[title]].amount += Number(amount) || 0;
        return;
      }
      seen[title] = discountCodes.length;
      discountCodes.push({ title: title, amount: Number(amount) || 0 });
    }

    // Cart-level applications. `discount_applications` is the canonical list;
    // `cart_level_discount_applications` is included as a fallback for older
    // payloads that only populate the cart-level array.
    var cartLevel = [].concat(
      Array.isArray(cart.discount_applications) ? cart.discount_applications : [],
      Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []
    );
    cartLevel.forEach(function (entry) {
      if (!entry || entry.type !== 'discount_code') return;
      pushCode(entry.title || entry.code, entry.total_allocated_amount);
    });

    var items = cart.items.map(function (item) {
      // Per-line discount codes live on `line_level_discount_allocations`. The
      // older `discounts` field is kept as a fallback for legacy storefronts.
      var allocations = Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      var itemCodes = [];
      allocations.forEach(function (allocation) {
        var application = allocation && allocation.discount_application;
        if (!application || application.type !== 'discount_code') return;
        var title = application.title || application.code || '';
        var amount = allocation.amount || 0;
        itemCodes.push({ title: title, amount: amount });
        pushCode(title, amount);
      });
      if (!itemCodes.length && Array.isArray(item.discounts)) {
        item.discounts.forEach(function (legacy) {
          if (!legacy || !legacy.title) return;
          itemCodes.push({ title: legacy.title, amount: legacy.amount || 0 });
          pushCode(legacy.title, legacy.amount);
        });
      }

      return {
        id: item.id,
        key: item.key,
        title: item.product_title || item.title,
        variant: item.variant_options ? item.variant_options.filter(function (option) { return option && option !== 'Default Title'; }).join(' / ') : (item.variant_title || ''),
        quantity: item.quantity,
        image: item.image || (item.featured_image && item.featured_image.url) || '',
        finalLinePrice: item.final_line_price != null ? item.final_line_price : item.line_price,
        originalLinePrice: item.original_line_price != null ? item.original_line_price : item.line_price,
        lineDiscount: item.line_level_total_discount || 0,
        discountCodes: itemCodes
      };
    });

    return {
      currency: cart.currency || 'EUR',
      itemCount: cart.item_count || 0,
      itemsSubtotal: cart.items_subtotal_price || cart.total_price || 0,
      totalPrice: cart.total_price || 0,
      totalDiscount: cart.total_discount || 0,
      discountCodes: discountCodes,
      items: items
    };
  }

  function renderLineItems(container, snapshot, options) {
    if (!container) return;
    var summary = container.closest('[data-summary]');
    var template = summary ? summary.querySelector('[data-line-item-template]') : null;
    if (!template) return;

    var loading = container.querySelector('[data-summary-loading]');
    if (loading) loading.remove();

    container.querySelectorAll('.fake-checkout-line').forEach(function (node) { node.remove(); });

    if (!snapshot || !snapshot.items.length) return;

    snapshot.items.forEach(function (item) {
      var clone = template.content.firstElementChild.cloneNode(true);
      var image = clone.querySelector('[data-line-image]');
      var quantity = clone.querySelector('[data-line-quantity]');
      var title = clone.querySelector('[data-line-title]');
      var variant = clone.querySelector('[data-line-variant]');
      var price = clone.querySelector('[data-line-price]');
      var priceOriginal = clone.querySelector('[data-line-price-original]');
      var discount = clone.querySelector('[data-line-discount]');
      var discountAmount = clone.querySelector('[data-line-discount-amount]');

      if (image) {
        if (item.image) {
          image.src = item.image;
          image.alt = item.title || '';
        } else {
          image.remove();
        }
      }
      if (quantity) quantity.textContent = item.quantity;
      if (title) title.textContent = item.title || '';
      if (variant) {
        if (item.variant) {
          variant.textContent = item.variant;
        } else {
          variant.remove();
        }
      }

      if (price) price.textContent = formatMoney(item.finalLinePrice, snapshot.currency);

      var realCodes = (item.discountCodes || []);

      if (realCodes.length && discount) {
        // Real per-line discount codes (`line_level_discount_allocations` with
        // type `discount_code`). Clone the badge row once per code so each
        // applied code is surfaced with its actual title and allocated amount.
        var parent = discount.parentNode;
        realCodes.forEach(function (code, idx) {
          var node = idx === 0 ? discount : discount.cloneNode(true);
          node.hidden = false;
          var labelEl = node.querySelector('.fake-checkout-line__discount-label');
          var amountEl = node.querySelector('[data-line-discount-amount]');
          if (labelEl) labelEl.textContent = code.title;
          if (amountEl) amountEl.textContent = '-' + formatMoney(code.amount, snapshot.currency);
          if (idx > 0) parent.appendChild(node);
        });
        if (priceOriginal && item.originalLinePrice && item.originalLinePrice > item.finalLinePrice) {
          priceOriginal.hidden = false;
          priceOriginal.textContent = formatMoney(item.originalLinePrice, snapshot.currency);
        }
      } else if (item.originalLinePrice && item.originalLinePrice > item.finalLinePrice) {
        if (priceOriginal) {
          priceOriginal.hidden = false;
          priceOriginal.textContent = formatMoney(item.originalLinePrice, snapshot.currency);
        }
      }

      container.appendChild(clone);
    });
  }

  function renderAppliedDiscounts(scope, snapshot, opts) {
    if (!scope || !snapshot) return;
    var container = scope.querySelector('[data-applied-discounts]');
    var template = scope.querySelector('[data-applied-discount-template]');
    if (!container || !template) return;

    container.innerHTML = '';

    var removed = (opts && opts.removedCodes) || {};
    var codes = (snapshot.discountCodes || []).filter(function (entry) {
      return entry && entry.title && !removed[entry.title];
    });

    if (!codes.length) {
      container.hidden = true;
      return;
    }

    codes.forEach(function (entry) {
      var clone = template.content.firstElementChild.cloneNode(true);
      var title = clone.querySelector('[data-applied-discount-title]');
      if (title) title.textContent = entry.title;
      clone.setAttribute('data-applied-discount', entry.title);
      container.appendChild(clone);
    });

    container.hidden = false;
  }

  function renderTotals(scope, snapshot, opts, strings) {
    if (!scope || !snapshot) return;
    var totals = scope.querySelector('[data-totals]');
    var subtotal = scope.querySelector('[data-subtotal]');
    var subtotalLabel = scope.querySelector('[data-subtotal-label]');
    var total = scope.querySelector('[data-total]');
    var currency = scope.querySelector('[data-currency]');
    var savings = scope.querySelector('[data-savings]');
    var savingsAmount = scope.querySelector('[data-savings-amount]');
    var tax = scope.querySelector('[data-tax]');

    if (!totals) return;
    totals.hidden = false;

    if (subtotalLabel && strings && strings.subtotalLabel) {
      subtotalLabel.textContent = pluralizeSubtotalLabel(strings.subtotalLabel, snapshot.itemCount);
    }
    if (subtotal) subtotal.textContent = formatMoney(snapshot.itemsSubtotal, snapshot.currency);

    var totalAmount = snapshot.totalPrice;
    if (total) total.textContent = formatAmount(totalAmount);
    if (currency) currency.textContent = snapshot.currency;

    if (savings) {
      var savedAmount = snapshot.totalDiscount;
      if (savedAmount > 0) {
        savings.hidden = false;
        if (savingsAmount) savingsAmount.textContent = formatMoney(savedAmount, snapshot.currency);
      } else {
        savings.hidden = true;
      }
    }

    if (tax) {
      var rate = strings && strings.taxRate ? Number(strings.taxRate) : 0;
      var template = strings && strings.taxTemplate ? strings.taxTemplate : '';
      if (rate > 0 && template) {
        // Prices in Shopify EU markets are gross (VAT included). Reverse out the
        // included portion: tax = gross * rate / (100 + rate).
        var taxAmount = totalAmount * rate / (100 + rate);
        tax.textContent = template
          .replace('{{ tax }}', formatMoney(taxAmount, snapshot.currency))
          .replace('{{tax}}', formatMoney(taxAmount, snapshot.currency));
        tax.hidden = false;
      } else {
        tax.hidden = true;
      }
    }
  }

  function setFieldError(field, message) {
    if (!field) return;
    var errorEl = field.querySelector('[data-field-error]');
    var input = field.querySelector('input, textarea, select');
    if (input) {
      input.classList.toggle('is-invalid', Boolean(message));
      if (message) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }
    if (errorEl) {
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }
  }

  function validateField(field) {
    if (!field) return true;
    var input = field.querySelector('input, textarea, select');
    if (!input) return true;
    var validateType = input.getAttribute('data-validate');
    var requiredMessage = input.getAttribute('data-error-required');
    var invalidMessage = input.getAttribute('data-error-invalid');

    if (validateType === 'checkbox') {
      if (input.required && !input.checked) {
        setFieldError(field, requiredMessage || invalidMessage || '');
        return false;
      }
      setFieldError(field, '');
      return true;
    }

    var value = (input.value || '').trim();
    if (input.required && !value) {
      setFieldError(field, requiredMessage || invalidMessage || '');
      return false;
    }
    var pattern = VALIDATION_PATTERNS[validateType];
    if (value && pattern && !pattern.test(value)) {
      setFieldError(field, invalidMessage || requiredMessage || '');
      return false;
    }
    setFieldError(field, '');
    return true;
  }

  function gatherFormData(form) {
    var data = {};
    var inputs = form.querySelectorAll('input, textarea, select');
    inputs.forEach(function (input) {
      if (!input.name) return;
      if (input.type === 'checkbox') {
        data[input.name] = input.checked;
      } else {
        data[input.name] = input.value;
      }
    });
    return data;
  }

  // ----- Checkout custom element ---------------------------------------------------
  var FakeCheckout = (function () {
    function init(root) {
      var form = root.querySelector('[data-fake-checkout-form]');
      var summary = root.querySelector('[data-summary]');
      var emptyState = summary ? summary.querySelector('[data-empty]') : null;
      var totalsEl = summary ? summary.querySelector('[data-totals]') : null;
      var submitButton = root.querySelector('[data-submit]');

      var submitEndpoint = SUBMIT_ENDPOINT;
      var cartUpdateUrl = root.getAttribute('data-cart-update-url') || DEFAULT_CART_UPDATE_URL;
      var thankYouUrl = root.getAttribute('data-thank-you-url') || '/pages/thank-you-page';
      var cartUrl = root.getAttribute('data-cart-url') || '/cart.js';
      var defaultCurrency = root.getAttribute('data-currency') || 'EUR';

      var state = {
        cart: null,
        // Titles being removed (waiting for /cart/update.js to confirm). Keeps
        // the chip filtered out optimistically so the UI feels instant.
        removedCodes: {}
      };

      var strings = readStrings(root);

      function render() {
        if (!state.cart || !state.cart.items.length) {
          if (emptyState) emptyState.hidden = false;
          if (totalsEl) totalsEl.hidden = true;
          if (submitButton) submitButton.disabled = true;
          var itemsContainerEmpty = summary ? summary.querySelector('[data-summary-items]') : null;
          if (itemsContainerEmpty) {
            var loadingEl = itemsContainerEmpty.querySelector('[data-summary-loading]');
            if (loadingEl) loadingEl.remove();
          }
          return;
        }
        if (emptyState) emptyState.hidden = true;
        if (submitButton) submitButton.disabled = false;
        var itemsContainer = summary.querySelector('[data-summary-items]');
        var renderOpts = { removedCodes: state.removedCodes };
        renderLineItems(itemsContainer, state.cart, renderOpts);
        renderAppliedDiscounts(summary, state.cart, renderOpts);
        renderTotals(summary, state.cart, renderOpts, strings);
      }

      function loadCart() {
        fetchCart(cartUrl)
          .then(function (cart) {
            state.cart = normaliseCart(cart);
            if (state.cart && !state.cart.currency) state.cart.currency = defaultCurrency;
            render();
          })
          .catch(function () {
            state.cart = null;
            render();
          });
      }

      // Collects the codes currently applied to the cart so we can preserve them
      // when /cart/update.js replaces the full discount list on every call.
      function currentCodeTitles() {
        return (state.cart && state.cart.discountCodes ? state.cart.discountCodes : [])
          .map(function (entry) { return entry.title; })
          .filter(function (title) { return title && !state.removedCodes[title]; });
      }

      // Sends the desired discount-code list to Shopify and re-renders from the
      // response. Returns the normalised cart so callers can derive success.
      function postDiscounts(codes) {
        return fetch(cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: codes.join(',') })
        })
          .then(function (response) { return response.json().catch(function () { return null; }); })
          .then(function (cart) {
            if (!cart) return null;
            var nextCart = normaliseCart(cart);
            if (!nextCart) return null;
            if (!nextCart.currency) nextCart.currency = defaultCurrency;
            state.cart = nextCart;
            state.removedCodes = {};
            return nextCart;
          });
      }

      function applyDiscount() {
        var input = summary.querySelector('[data-discount-input]');
        var message = summary.querySelector('[data-discount-message]');
        var wrapper = summary.querySelector('[data-discount-state]');
        if (!input) return;
        var code = (input.value || '').trim();
        if (!code) return;

        wrapper.setAttribute('data-discount-state', 'pending');

        // /cart/update.js with `discount` replaces ALL applied codes, so we
        // include the existing codes alongside the new one. Shopify normalises
        // casing in the response — we compare case-insensitively below.
        var existing = currentCodeTitles();
        var lowered = code.toLowerCase();
        var alreadyApplied = existing.some(function (title) {
          return title.toLowerCase() === lowered;
        });
        var combined = alreadyApplied ? existing : existing.concat([code]);

        postDiscounts(combined)
          .then(function (nextCart) {
            var success = !!(nextCart && nextCart.discountCodes.some(function (entry) {
              return entry.title && entry.title.toLowerCase() === lowered;
            }));
            wrapper.setAttribute('data-discount-state', success ? 'success' : 'error');
            if (message) {
              message.hidden = false;
              message.textContent = success ? strings.discountSuccess : strings.discountError;
            }
            if (success) input.value = '';
            render();
          })
          .catch(function () {
            wrapper.setAttribute('data-discount-state', 'error');
            if (message) {
              message.hidden = false;
              message.textContent = strings.discountError;
            }
            render();
          });
      }

      function removeDiscount(code) {
        if (!code) return;

        // Optimistically hide the chip so the UI feels instant. /cart/update.js
        // replaces ALL applied codes on every call, so we send the remaining
        // titles joined by a comma — an empty string clears every discount per
        // the Ajax docs.
        state.removedCodes[code] = true;
        render();

        postDiscounts(currentCodeTitles())
          .then(function (nextCart) {
            if (!nextCart) return;
            render();
          })
          .catch(function () {
            delete state.removedCodes[code];
            render();
          });
      }

      function validateForm() {
        var fields = form.querySelectorAll('[data-field]');
        var firstInvalid = null;
        fields.forEach(function (field) {
          var valid = validateField(field);
          if (!valid && !firstInvalid) firstInvalid = field;
        });
        if (firstInvalid) {
          var input = firstInvalid.querySelector('input, textarea, select');
          if (input && typeof input.focus === 'function') input.focus();
        }
        return !firstInvalid;
      }

      function submit(event) {
        event.preventDefault();
        if (!validateForm()) return;
        if (!state.cart || !state.cart.items.length) return;

        submitButton.disabled = true;
        var label = submitButton.querySelector('[data-submit-label]');
        if (label && strings.submittingLabel) {
          label.dataset.original = label.textContent;
          label.textContent = strings.submittingLabel;
        }

        var payload = {
          form: gatherFormData(form),
          cart: state.cart
        };

        fetch(submitEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        })
          .then(function (response) {
            return response.json().catch(function () { return {}; }).then(function (data) {
              if (!response.ok) {
                throw new Error(data.message || 'submission failed');
              }
              return data;
            });
          })
          .then(function (data) {
            if (data && data.data && data.data.id) {
              writeSnapshot({
                cart: state.cart,
                form: payload.form,
                timestamp: Date.now()
              });
              try { window.sessionStorage.removeItem(CART_CLEARED_KEY); } catch (err) { /* ignore */ }
              window.location.assign(thankYouUrl);
            } else {
              throw new Error('submission failed');
            }
          })
          .catch(function () {
            submitButton.disabled = false;
            if (label && label.dataset.original) {
              label.textContent = label.dataset.original;
            }
            var firstField = form.querySelector('[data-field]');
            if (firstField) {
              setFieldError(firstField, strings.submitError);
            }
          });
      }

      // Wire events
      form.addEventListener('submit', submit);
      form.addEventListener('input', function (event) {
        var field = event.target.closest('[data-field]');
        if (field) validateField(field);
      });
      form.addEventListener('change', function (event) {
        var field = event.target.closest('[data-field]');
        if (field) validateField(field);
      });

      var applyButton = summary.querySelector('[data-discount-apply]');
      var discountInput = summary.querySelector('[data-discount-input]');
      if (applyButton) applyButton.addEventListener('click', applyDiscount);
      if (discountInput) {
        discountInput.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            applyDiscount();
          }
        });
      }

      // Event delegation for the chip X buttons — the chips themselves are
      // dynamically cloned from the template on every render so we listen on
      // the stable container.
      var appliedDiscountsContainer = summary.querySelector('[data-applied-discounts]');
      if (appliedDiscountsContainer) {
        appliedDiscountsContainer.addEventListener('click', function (event) {
          var button = event.target.closest('[data-applied-discount-remove]');
          if (!button) return;
          var chip = button.closest('[data-applied-discount]');
          if (!chip) return;
          removeDiscount(chip.getAttribute('data-applied-discount'));
        });
      }

      loadCart();
    }

    function readStrings(root) {
      // The summary template embeds translated text already so we only need a few
      // strings for dynamic states (success / error fallback messages, the
      // submit error fallback, and the subtotal label that needs interpolation).
      var subtotalLabelEl = root.querySelector('[data-subtotal-label]');
      return {
        subtotalLabel: subtotalLabelEl ? subtotalLabelEl.textContent : '',
        discountSuccess: root.getAttribute('data-discount-success-text') || '',
        discountError: root.getAttribute('data-discount-error-text') || '',
        submitError: root.getAttribute('data-submit-error-text') || '',
        submittingLabel: root.getAttribute('data-submit-loading-text') || '',
        taxTemplate: root.getAttribute('data-tax-template') || '',
        taxRate: root.getAttribute('data-tax-rate') || '0'
      };
    }

    return { init: init };
  })();

  // ----- Thank-you custom element --------------------------------------------------
  var FakeThankYou = (function () {
    function init(root) {
      var summary = root.querySelector('[data-summary]');
      var cartUrl = root.getAttribute('data-cart-url') || '/cart.js';
      var cartClearUrl = root.getAttribute('data-cart-clear-url') || '/cart/clear.js';
      var defaultCurrency = root.getAttribute('data-currency') || 'EUR';

      var snapshot = readSnapshot();
      var renderState = function (data, opts) {
        if (!summary) return;
        if (!data || !data.items.length) {
          var emptyEl = summary.querySelector('[data-empty]');
          var loadingEl = summary.querySelector('[data-summary-loading]');
          if (loadingEl) loadingEl.remove();
          if (emptyEl) emptyEl.hidden = false;
          return;
        }
        var itemsContainer = summary.querySelector('[data-summary-items]');
        var renderOpts = opts || {};
        renderLineItems(itemsContainer, data, renderOpts);
        renderAppliedDiscounts(summary, data, renderOpts);
      };

      function clearShopifyCart() {
        var alreadyCleared = false;
        try { alreadyCleared = window.sessionStorage.getItem(CART_CLEARED_KEY) === '1'; } catch (err) { /* ignore */ }
        if (alreadyCleared) return;

        fetch(cartClearUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin'
        })
          .then(function () {
            try { window.sessionStorage.setItem(CART_CLEARED_KEY, '1'); } catch (err) { /* ignore */ }
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .catch(function () {
            // ignore - the shop may handle this asynchronously
          });
      }

      if (snapshot && snapshot.cart) {
        if (!snapshot.cart.currency) snapshot.cart.currency = defaultCurrency;
        renderState(snapshot.cart);
        clearShopifyCart();
        clearSnapshot();
      } else {
        fetchCart(cartUrl)
          .then(function (cart) {
            var normalised = normaliseCart(cart);
            if (normalised && !normalised.currency) normalised.currency = defaultCurrency;
            renderState(normalised);
            clearShopifyCart();
          })
          .catch(function () {
            renderState(null);
          });
      }
    }

    return { init: init };
  })();

  // ----- Custom element registration ----------------------------------------------
  if (!window.customElements.get('fake-checkout')) {
    window.customElements.define('fake-checkout', class extends HTMLElement {
      connectedCallback() {
        FakeCheckout.init(this);
      }
    });
  }

  if (!window.customElements.get('fake-thank-you')) {
    window.customElements.define('fake-thank-you', class extends HTMLElement {
      connectedCallback() {
        FakeThankYou.init(this);
      }
    });
  }
})();
